import {
  Alert,
  AlertTitle,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material'
import { filter } from 'ramda'
import { Trans, useTranslation } from 'react-i18next'

import { SubmitButton } from '@cibo/ui'
import { Form, Formik } from 'formik'

import { GridRowId } from '@mui/x-data-grid-pro'
import { AxiosError } from 'axios'
import { useSnackbar } from 'notistack'
import { useEffect } from 'react'
import { useDeleteFieldsById, useFields } from '../../queries'

interface DeleteProps {
  onRequestClose(): void
  selectedIds?: GridRowId[]
  visible: boolean
}

export type DeleteError = AxiosError<
  Record<
    string,
    { errors: { code: number; error: string; id: string; name: string }[]; deleteIds: string[] }[]
  >
>

export interface LandDeleteFormProps extends DeleteProps {
  error?: DeleteError
  isLoading?: boolean
  onSuccess(): void
}

const LandDeleteFormModal = ({
  error,
  isLoading,
  onRequestClose,
  selectedIds = [],
  visible,
}: LandDeleteFormProps) => {
  const { t } = useTranslation('@cibo/landmanager/LandDeleteForm')
  const { data } = useFields(selectedIds as string[])

  const fieldErrors =
    error?.response?.data?.results &&
    filter(response => !!(response?.errors?.length > 0), error.response.data.results)

  return (
    <Dialog open={visible} fullWidth maxWidth="xs">
      <DialogTitle>{t('areYouSure', { count: selectedIds?.length })}</DialogTitle>
      <DialogContent>
        {fieldErrors && Object.keys(fieldErrors).length > 0 ? (
          <Alert severity="error">
            <AlertTitle>{t('deleteError', { count: selectedIds.length })}</AlertTitle>
            <Typography>
              {Object.values(fieldErrors)
                .map(({ errors }) =>
                  errors
                    .map(({ error, id }) =>
                      error.replace(id, data?.find(field => field.id === id)?.name)
                    )
                    .join(', ')
                )
                .join(',')}
            </Typography>
          </Alert>
        ) : (
          <Typography>
            <Trans
              i18nKey="@cibo/landmanager/LandDeleteForm:confirmDelete"
              count={selectedIds?.length}
            >
              are you sure?
              <b>can't undo</b>
            </Trans>
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onRequestClose}>{t('cancel')}</Button>

        <SubmitButton
          style={{
            marginTop: 0,
          }}
          loading={isLoading}
          disabled={fieldErrors && Object.keys(fieldErrors).length > 0}
          color="error"
          testId="confirm_delete_button"
        >
          {t('delete')}
        </SubmitButton>
      </DialogActions>
    </Dialog>
  )
}

export const LandDeleteForm = (props: LandDeleteFormProps) => {
  const { t } = useTranslation('@cibo/landmanager/LandDeleteForm')
  const deleteFieldsById = useDeleteFieldsById()
  const { enqueueSnackbar } = useSnackbar()

  const { onRequestClose, onSuccess, selectedIds = [] } = props

  useEffect(() => {
    if (props.visible) {
      deleteFieldsById.reset()
    }
  }, [props.visible])

  const handleDeleteSelected = async () => {
    if (selectedIds.length > 0) {
      deleteFieldsById.mutate(selectedIds as string[], {
        onSuccess: () => {
          onSuccess()
          onRequestClose()
          enqueueSnackbar(t('deleteFields', { count: selectedIds.length }), {
            variant: 'success',
          })
        },
      })
    }
  }

  return (
    <Formik initialValues={{}} onSubmit={handleDeleteSelected}>
      <Form>
        <LandDeleteFormModal {...props} />
      </Form>
    </Formik>
  )
}

export default LandDeleteForm
